<div class="config-page">
    @if (loading()) {
    <div class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Loading configuration...</span>
    </div>
    } @else if (error()) {
    <p-message severity="error">{{ error()! }}</p-message>
    } @else {
    <div class="config-layout">
        <!-- LEFT: Editor Panel -->
        <app-config-editor
            [config]="configData()"
            [schema]="configSchema()"
            [files]="fileOptions()"
            [selectedFile]="selectedFilePath()"
            [readonly]="!tunnelConnected()"
            [canEdit]="!isDefault()"
            [canDelete]="!isDefault()"
            [loading]="editorLoading()"
            [saving]="editorSaving()"
            [errorMessage]="editorError()"
            [newFileSuffix]="newFileSuffixText()"
            [downloadFileName]="downloadName()"
            [lastSaved]="lastRevisionTime()"
            [isDefault]="isDefault()"
            [restartRequired]="restartRequired()"
            [configPath]="configPath()"
            [templateDisabled]="!tunnelConnected()"
            (fileSelected)="switchFile($event)"
            (save)="save($event)"
            (reloadRequested)="reload()"
            (createFile)="createNewConfig($event)"
            (deleteFile)="deleteConfig()"
            (templateClick)="showTemplateWizard()">
            <span editor-badge class="connection-badge" [class.connected]="tunnelConnected()" [class.disconnected]="!tunnelConnected()">
                <i class="pi" [class.pi-bolt]="tunnelConnected()" [class.pi-cloud-download]="!tunnelConnected()"></i>
                {{ tunnelConnected() ? 'Live' : 'Offline' }}
            </span>
        </app-config-editor>

        <!-- RIGHT: History Panel -->
        <div class="history-panel">
            <div class="history-header">
                <h4>History</h4>
                <p-button icon="pi pi-refresh" [text]="true" size="small" pTooltip="Refresh" (onClick)="refreshHistory()" />
            </div>

            <div class="history-tree">
                @if (historyTree().length === 0) {
                <div class="no-history">No history available</div>
                } @else { @for (node of historyTree(); track node.fileName) {
                <div class="tree-node">
                    <div class="tree-node-header" (click)="toggleTreeNode(node)" [class.active]="node.isActive">
                        <i class="fa" [class.fa-chevron-down]="node.expanded" [class.fa-chevron-right]="!node.expanded"></i>
                        <i class="fa fa-file"></i>
                        <span class="node-filename">{{ node.fileName }}</span>
                        @if (node.isActive) {
                        <span class="active-indicator">active</span>
                        }
                    </div>
                    @if (node.expanded) {
                    <div class="tree-node-children">
                        @for (version of node.versions; track version.id) {
                        <div class="tree-version" [class.current]="version.isCurrent">
                            <div class="version-info" (click)="compareVersion(node.fileName, version)">
                                <span class="version-date">{{ version.lastEditedUtc | date:'short' }}</span>
                                @if (version.isCurrent) {
                                <span class="current-badge">current</span>
                                } @if (version.isDefault) {
                                <span class="default-badge">default</span>
                                }
                            </div>
                            <div class="version-actions">
                                <p-button icon="fa fa-download" [text]="true" size="small" (onClick)="downloadVersion(node.fileName, version)" pTooltip="Download" />
                            </div>
                        </div>
                        }
                    </div>
                    }
                </div>
                } }
            </div>
        </div>
    </div>
    }
</div>

<!-- Compare Dialog -->
<p-dialog [header]="'Compare: ' + compareFileName()" [(visible)]="compareVisible" [modal]="true" [style]="{ width: '90vw', height: '85vh' }" [maximizable]="true" (onHide)="closeCompare()">
    <div class="compare-container">
        @if (compareLoading()) {
        <div class="compare-loading">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading...</span>
        </div>
        } @else if (compareHistoricalConfig() && compareCurrentConfig()) {
        <p class="compare-description">
            Showing changes between the selected version ({{ compareDate() | date:'medium' }}) and the current configuration. Lines in <span class="text-red">red</span> were removed, lines in <span class="text-green">green</span> were added.
        </p>
        <div class="diff-header">
            <div class="diff-header-labels">
                <span class="diff-label old"><i class="pi pi-minus-circle"></i> Historical ({{ compareDate() | date:'medium' }})</span>
                <span class="diff-label new"><i class="pi pi-plus-circle"></i> Current</span>
            </div>
            <div class="diff-stats">
                @if (diffLines(); as lines) {
                <span class="stat-added">+{{ lines.additions }}</span>
                <span class="stat-removed">-{{ lines.deletions }}</span>
                }
            </div>
        </div>

        <div class="unified-diff">
            @if (diffLines(); as diff) {
            <table class="diff-table">
                <tbody>
                    @for (line of diff.lines; track $index) {
                    <tr [class]="'diff-line diff-' + line.type">
                        <td class="line-num old-num">{{ line.oldNum || '' }}</td>
                        <td class="line-num new-num">{{ line.newNum || '' }}</td>
                        <td class="line-indicator">{{ line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ' }}</td>
                        <td class="line-content"><pre>{{ line.content }}</pre></td>
                    </tr>
                    }
                </tbody>
            </table>
            }
        </div>
        }
    </div>
    <ng-template #footer>
        <p-button label="Close" severity="secondary" (onClick)="closeCompare()" />
    </ng-template>
</p-dialog>

<!-- Template Wizard -->
<app-config-wizard [visible]="templateWizardVisible()" [deviceId]="currentDeviceId()" (visibleChange)="templateWizardVisible.set($event)" (templateApplied)="onTemplateApplied()" />
